<template>
  <div
    class="rf-datepicker &_.rf-menu--button]:relative [&_.rf-menu--button]:flex [&_.rf-menu--button]:items-center [&_.rf-menu--button]:gap-2 [&_.rf-menu--button]:rounded-lg [&_.rf-menu--button]:py-2 [&_.rf-menu--button]:pr-6! [&_button.rf-menu--button]:pl-8!"
  >
    <RfMenu
      ref="menu"
      preset="datePicker"
      buttonType="white"
      variant="outlined"
      buttonSize="base"
      :disabled="lockPresetDate ? lockWithPresetMessage : null"
      ariaHaspopup="dialog"
      role="dialog"
      data-cy="datepicker"
      @show="initDate = localDate"
      @hide="resetCalendar()"
    >
      <template #activator>
        <RfDateIcon class="h-6! w-6!" />
        <span class="text-body mx-2 flex" data-cy="datepicker--preset-value">
          {{ (lockPresetDate ? lockPresetDate : localDate).preset?.name }}
          <span v-if="!hideCustom && (lockPresetDate ? lockPresetDate : localDate).preset?.name">
            {{
              `: ${(lockPresetDate ? lockPresetDate : localDate).startDate?.format(`${dateOrder()}/YYYY`) || "Start date"} – ${
                (lockPresetDate ? lockPresetDate : localDate).endDate?.format(
                  `${dateOrder()}/YYYY`,
                ) || "End date"
              }`
            }}
          </span>
        </span>
      </template>
      <template #menu="{ close }">
        <div
          class="text-body text-grey-1 flex max-w-max shrink grow-0"
          :style="{ minWidth: tooltipWidth }"
        >
          <div
            class="text-action-buttons text-grey-1 flex flex-col p-4"
            :class="{ 'w-full': hideCustom }"
          >
            <RfTooltip
              v-if="isStartDateInTheFuture && tooltipAnchorEl"
              ref="tooltipEl"
              :anchor="tooltipAnchorEl[0]"
            >
              <template #tooltip>
                <div>{{ tooltipMessage }}</div>
              </template>
            </RfTooltip>
            <template v-if="!hideCustom">
              <span>Custom</span>
              <span class="border-strokes my-4 border-t border-solid"></span>
            </template>
            <div class="list text-black-1! flex flex-col gap-4">
              <template
                v-for="period in MetricsUtils.All_Periods.filter(
                  el => !include || include.includes(el.value),
                )"
                :key="period.value"
              >
                <button
                  v-if="
                    (period.value !== 'since_start_date' || sinceStartDayRef) &&
                    period.value !== 'custom'
                  "
                  :ref="
                    period.value === 'since_start_date' && isStartDateInTheFuture
                      ? 'tooltipAnchorEl'
                      : undefined
                  "
                  :key="period.value"
                  :disabled="isButtonDisabled(period.value)"
                  class="rounded p-2 pr-10 text-left"
                  :class="{ 'bg-beige-1!': localDate.preset?.value === period.value }"
                  :style="{ color: isButtonDisabled(period.value) ? 'grey' : '' }"
                  :data-cy="`datepicker--${period.value}`"
                  v-bind="{
                    ...(period.value === 'since_start_date' && {
                      onFocus: () => showTooltip(),
                      onMouseenter: () => showTooltip(),
                      onBlur: () => tooltipEl?.hide?.(),
                      onMouseleave: () => tooltipEl?.hide?.(),
                    }),
                  }"
                  @click="(setTimeFromPreset(period.value), close())"
                >
                  <span class="inline-flex items-center gap-2 lowercase first-letter:uppercase">
                    <span class="lowercase first-letter:uppercase">
                      {{ period.name }}
                    </span>
                    <RfStatusIcon
                      v-if="period.value === 'since_start_date' && isStartDateInTheFuture"
                      class="fill-shade-3 mb-0.5 h-5! w-5!"
                    />
                  </span>
                </button>
              </template>
            </div>
          </div>
          <template v-if="!hideCustom">
            <span class="border-strokes border-l border-solid"></span>
            <div class="flex flex-col px-6 py-4">
              <div class="flex grow-0 items-center gap-4">
                <div class="text-black-2 flex flex-1 flex-col gap-1">
                  <span class="font-semibold">Start date</span>
                  <span class="bg-beige-1 rounded-md px-4 py-2 text-base">
                    {{
                      (selectedDates.startDate ? selectedDates : localDate).startDate?.format(
                        `${dateOrder()}/YYYY`,
                      ) || "Start date"
                    }}
                  </span>
                </div>
                <div class="text-black-2 flex flex-1 flex-col gap-1">
                  <span class="font-semibold">End date</span>
                  <span class="bg-beige-1 rounded-md px-4 py-2 text-base">
                    {{
                      (selectedDates.startDate ? selectedDates : localDate).endDate?.format(
                        `${dateOrder()}/YYYY`,
                      ) || "End date"
                    }}
                  </span>
                </div>
              </div>
              <div class="my-7 flex w-full grow flex-col">
                <div class="mb-3 flex grow-0 justify-between">
                  <span class="text-heading-2 text-black-1 font-semibold"
                    >{{ calendarDate.format("MMMM YYYY") }}
                  </span>
                  <span class="flex grow-0 items-center gap-2">
                    <button aria-label="Previous month" @click="changeCalendarMonth(-1)">
                      <RfChevronIcon class="h-6! w-6!" />
                    </button>
                    <button aria-label="Next month" @click="changeCalendarMonth(1)">
                      <RfChevronIcon class="h-6! w-6!" style="transform: rotate(180deg)" />
                    </button>
                  </span>
                </div>
                <div class="text-body text-center">
                  <ul class="grid list-none grid-cols-7">
                    <li class="text-beige-40 px-2.5 py-3">Sun</li>
                    <li class="text-beige-40 px-2.5 py-3">Mon</li>
                    <li class="text-beige-40 px-2.5 py-3">Tue</li>
                    <li class="text-beige-40 px-2.5 py-3">Wed</li>
                    <li class="text-beige-40 px-2.5 py-3">Thu</li>
                    <li class="text-beige-40 px-2.5 py-3">Fri</li>
                    <li class="text-beige-40 px-2.5 py-3">Sat</li>
                  </ul>
                  <ul class="grid list-none grid-cols-7">
                    <li
                      v-for="_ in calendarData.lastMonthDates"
                      :key="`last-${_}`"
                      class="text-beige-40! relative flex"
                    >
                      <div
                        class="disabled:text-beige-40 flex grow items-center justify-center px-2.5 py-3 hover:bg-none"
                      ></div>
                    </li>
                    <li
                      v-for="date in calendarData.thisMonthDates"
                      :key="`curr-${date}`"
                      class="relative flex"
                      :class="{
                        'rf-datepicker--day-selected before:bg-yellow text-black-1! before:absolute before:inset-x-0 before:top-1/2 before:h-8':
                          (selectedDates.startDate || selectedDates.endDate) &&
                          date.isBetween(
                            selectedDates.startDate || selectedDates.endDate,
                            selectedDates.endDate || selectedDates.startDate,
                            'day',
                            '[]',
                          ),
                        'before:rounded-l-full':
                          selectedDates.startDate && date.isSame(selectedDates.startDate, 'day'),
                        'before:rounded-r-full':
                          selectedDates.endDate && date.isSame(selectedDates.endDate, 'day'),
                      }"
                    >
                      <button
                        class="disabled:text-beige-40! flex grow items-center justify-center px-2.5 py-3 hover:bg-none disabled:cursor-default!"
                        :disabled="isDateDisabled(date)"
                        @click="selectCalendarDate(date)"
                      >
                        {{ date.date() }}
                      </button>
                    </li>
                    <li
                      v-for="_ in calendarData.nextMonthDates"
                      :key="`next-${_}`"
                      class="text-beige-40! relative flex"
                    >
                      <div
                        class="disabled:text-beige-40 flex grow items-center justify-center px-2.5 py-3 hover:bg-none"
                      ></div>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="mt-auto mb-0 flex w-full grow-0 gap-4">
                <RfButtonNew
                  class="mr-0 ml-auto flex-1"
                  type="quaternary"
                  size="large"
                  text="Cancel"
                  @click="(resetCalendar(), close())"
                />
                <RfButtonNew
                  class="mr-0 ml-0 flex-1"
                  type="secondary"
                  size="large"
                  text="Save"
                  @click="(save(), close())"
                />
              </div>
            </div>
          </template>
        </div>
      </template>
    </RfMenu>
  </div>
</template>

<script setup>
import MetricsUtils from "@/utils/MetricsUtils";
import { getTimeFromPreset } from "@/utils/TimeUtils";
import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { dateOrder } from "@/utils/DateDisplayUtils";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfChevronIcon from "@/components/icons/RfChevronIcon.vue";
import RfDateIcon from "@/components/icons/RfDateIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfStatusIcon from "@/components/icons/RfStatusIcon.vue";
import dayjs from "dayjs";
import timezones from "@/utils/constants/TimezonesConstants";
import { useAppsStore } from "@/pinia/appsStore";

const emit = defineEmits(["input"]);
const props = defineProps({
  sinceStartDayRef: { type: String, default: null },
  hideCustom: { type: Boolean, default: false },
  include: { type: [Array, null], default: null },
  min: { type: Object, default: null },
  max: { type: Object, default: null },
  preset: { type: String, default: null },
  lockWithPreset: { type: [String, Boolean], default: false },
  lockWithPresetMessage: { type: String, default: null },
});

const tooltipEl = ref();
const tooltipAnchorEl = ref();
const tooltipWidth = ref("100%");
const menu = ref({});
const localDate = ref({});
const initDate = ref({});
const calendarDate = ref(null);
const selectedDates = reactive({ startDate: null, endDate: null });
const tooltipMessage = ref("");

const lockPresetDate = computed(() => {
  if (!props.lockWithPreset) return false;
  const { startDate, endDate, preset } = getTimeFromPreset(
    props.lockWithPreset,
    props.sinceStartDayRef,
  );

  return {
    preset: MetricsUtils.All_Periods.find(el => el.value === preset),
    startDate,
    endDate,
  };
});

const isStartDateInTheFuture = computed(() => {
  const timezone = timezones[useAppsStore().app.timezone_offset] || "UTC";
  const startDate = dayjs(props.sinceStartDayRef).utc();
  const date = dayjs().tz(timezone).utc(true);
  return startDate.isAfter(date);
});

const calendarData = computed(() => {
  const firstDay = calendarDate.value.date(1).day();
  const lastDay = 6 - calendarDate.value.endOf("month").day();
  const lastDate = calendarDate.value.endOf("month").date();

  const lastMonthDates = [...Array(firstDay).keys()]
    .map(el => calendarDate.value.startOf("month").subtract(el + 1, "days"))
    .reverse();
  const thisMonthDates = [...Array(lastDate).keys()].map(el =>
    calendarDate.value.startOf("month").add(el, "days"),
  );
  const nextMonthDates = [...Array(lastDay).keys()].map(el =>
    calendarDate.value.endOf("month").add(el + 1, "days"),
  );
  return { lastMonthDates, thisMonthDates, nextMonthDates };
});

const calculateWidth = () => (tooltipWidth.value = `${menu.value.$el.clientWidth}px`);

const save = () => {
  if (selectedDates.startDate) {
    localDate.value = {
      preset: MetricsUtils.All_Periods.find(el => el.value === "custom"),
      startDate: selectedDates.startDate,
      endDate: selectedDates.endDate || selectedDates.startDate,
    };
  } else {
    selectedDates.startDate = selectedDates.endDate = null;
  }
  emit("input", localDate.value);
  initDate.value = localDate.value;
  nextTick(calculateWidth);
};

const showTooltip = () => {
  nextTick(() => {
    if (isStartDateInTheFuture.value) {
      tooltipMessage.value = "Start Date is in the future";
    }
    tooltipEl.value?.show?.();
  });
};

const isButtonDisabled = period => {
  return isStartDateInTheFuture.value && period === "since_start_date";
};

const setTimeFromPreset = incomingPreset => {
  if (props.lockWithPreset) return emit("input", lockPresetDate.value);
  const { startDate, endDate, preset } = getTimeFromPreset(incomingPreset, props.sinceStartDayRef);
  localDate.value = {
    preset: MetricsUtils.All_Periods.find(el => el.value === preset),
    startDate,
    endDate,
  };
  calendarDate.value = endDate;
  selectedDates.startDate = selectedDates.endDate = null;
  return save();
};

const changeCalendarMonth = (amount = 0) =>
  (calendarDate.value = calendarDate.value.add(amount, "month"));

const selectCalendarDate = date => {
  if ((selectedDates.startDate && selectedDates.endDate) || !selectedDates.startDate) {
    selectedDates.startDate = date;
    selectedDates.endDate = null;
  } else {
    selectedDates.endDate = date;
    if (+selectedDates.startDate > +selectedDates.endDate) {
      [selectedDates.startDate, selectedDates.endDate] = [
        selectedDates.endDate,
        selectedDates.startDate,
      ];
    }
  }
};
const resetCalendar = () => {
  if (initDate.value.preset?.value !== "custom") {
    selectedDates.startDate = selectedDates.endDate = null;
  } else {
    selectedDates.startDate = initDate.value.startDate;
    selectedDates.endDate = initDate.value.endDate;
  }

  localDate.value = initDate.value;
};

const isDateDisabled = date => {
  let minValue = false;
  let maxValue = false;
  if (props.min) minValue = date.isBefore(props.min);
  if (props.max) maxValue = date.isAfter(props.max);
  return minValue || maxValue;
};

onMounted(() => {
  calculateWidth();
  if (props.preset) setTimeFromPreset(props.preset);
});

watch(
  () => props.lockWithPreset,
  () => setTimeFromPreset(initDate.value.preset?.value),
);

defineExpose({ setTimeFromPreset });
</script>

<style lang="scss" scoped>
.rf-datepicker--day-selected {
  &:before {
    transform: translateY(-50%);
    content: "";
    z-index: -1;
    transition: background-color 0.3s ease-in-out;
  }
}
</style>
